/**
 * FuturisticLayoutGallery - Gallery component for selecting futuristic layouts
 * 
 * Displays all 15 futuristic layouts organized by category with preview cards.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Circle, Orbit, Hexagon, Square, Lightbulb, Waves, Box, Layers, 
  CreditCard, Sparkles, Droplet, Binary, Gem, Monitor, Brain,
  Check, Eye, Maximize2, X, Home, Settings, Users, BarChart3, 
  FileText, Bell, Search, Menu as MenuIcon
} from 'lucide-react';
import { 
  FuturisticLayoutType, 
  LayoutCategory, 
  LAYOUT_CATEGORIES, 
  LAYOUT_METADATA,
  getLayoutsByCategory,
  isFuturisticLayout
} from '../../types/layout.types';
import { useLayoutTemplates, useApplyLayoutTemplate } from '../../hooks/useLayoutTemplates';

// Preview wrapper that renders a simplified preview of the layout
// Each preview is designed to be visually distinct and showcase the unique characteristics of each layout
function LayoutPreview({ layoutType }: { layoutType: FuturisticLayoutType }) {

  const layoutPreviews: Record<string, React.ReactNode> = {
    BUBBLE_NAVIGATION: (
      <div className="w-full h-full bg-gradient-to-b from-surface-950 via-blue-950/20 to-surface-950 relative overflow-hidden flex flex-col">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Bubble Navigation</span>
        </div>
        {/* Main content area with mini cards */}
        <div className="flex-1 p-8 pt-12">
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            {['Dashboard', 'Transações', 'Usuários', 'Relatórios'].map((label, i) => (
              <div key={i} className="h-16 bg-surface-800/50 rounded-xl border border-surface-700 flex items-center justify-center">
                <span className="text-gray-400 text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Floating bubbles navigation at bottom */}
        <div className="pb-8 flex justify-center">
          <div className="flex gap-3 p-3 bg-surface-900/80 backdrop-blur-sm rounded-full border border-cyan-500/20">
            {[Home, BarChart3, Users, Settings, Bell].map((Icon, i) => (
              <motion.div
                key={i}
                className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
                  i === 0 
                    ? 'bg-gradient-to-br from-cyan-500 to-blue-500 shadow-cyan-500/40' 
                    : 'bg-surface-800 hover:bg-surface-700'
                }`}
                animate={{ y: i === 0 ? [0, -4, 0] : 0 }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Icon className={`w-5 h-5 ${i === 0 ? 'text-white' : 'text-gray-400'}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    ),
    ORBITAL: (
      <div className="w-full h-full bg-gradient-to-br from-surface-950 via-purple-950/10 to-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-purple-400 text-xs font-bold uppercase tracking-wider">Orbital Layout</span>
        </div>
        {/* Orbital rings with modules */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Outer orbit */}
          <motion.div
            className="absolute w-[280px] h-[280px] border border-cyan-500/20 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          >
            {[0, 90, 180, 270].map((angle, i) => (
              <motion.div
                key={i}
                className="absolute w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/80 to-blue-500/80 flex items-center justify-center shadow-lg shadow-cyan-500/20"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${angle}deg) translateX(140px) rotate(-${angle}deg) translate(-50%, -50%)`,
                }}
              >
                {[Home, BarChart3, Users, Settings][i] && 
                  React.createElement([Home, BarChart3, Users, Settings][i], { className: 'w-4 h-4 text-white' })}
              </motion.div>
            ))}
          </motion.div>
          {/* Inner orbit */}
          <motion.div
            className="absolute w-[160px] h-[160px] border border-purple-500/20 rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          >
            {[45, 135, 225, 315].map((angle, i) => (
              <motion.div
                key={i}
                className="absolute w-8 h-8 rounded-full bg-purple-500/60 shadow-lg shadow-purple-500/20"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${angle}deg) translateX(80px) rotate(-${angle}deg) translate(-50%, -50%)`,
                }}
              />
            ))}
          </motion.div>
          {/* Center hub */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center shadow-xl shadow-purple-500/30">
            <Home className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>
    ),
    HEXAGONAL_GRID: (
      <div className="w-full h-full bg-gradient-to-br from-surface-950 via-cyan-950/10 to-surface-950 p-6 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Hexagonal Grid</span>
        </div>
        {/* Hexagonal navigation grid */}
        <div className="h-full flex items-center justify-center">
          <div className="grid grid-cols-3 gap-2" style={{ transform: 'scale(0.9)' }}>
            {[
              { icon: Home, label: 'Home', active: true },
              { icon: BarChart3, label: 'Stats', active: false },
              { icon: Users, label: 'Users', active: false },
              { icon: FileText, label: 'Docs', active: false },
              { icon: Settings, label: 'Config', active: false },
              { icon: Bell, label: 'Alerts', active: false },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`w-20 h-20 flex flex-col items-center justify-center ${
                  item.active 
                    ? 'bg-gradient-to-br from-cyan-500/40 to-blue-500/40 border-cyan-400' 
                    : 'bg-surface-800/50 border-cyan-500/20'
                } border-2`}
                style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
                whileHover={{ scale: 1.1 }}
                animate={item.active ? { borderColor: ['rgba(34,211,238,0.6)', 'rgba(34,211,238,1)', 'rgba(34,211,238,0.6)'] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-cyan-300' : 'text-gray-400'}`} />
                <span className={`text-[10px] mt-1 ${item.active ? 'text-cyan-300' : 'text-gray-500'}`}>{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    ),
    GLASS_MORPHISM: (
      <div className="w-full h-full bg-gradient-to-br from-purple-950/30 via-surface-950 to-blue-950/30 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-purple-400 text-xs font-bold uppercase tracking-wider">Glass Morphism</span>
        </div>
        {/* Background blobs */}
        <motion.div 
          className="absolute top-10 left-10 w-48 h-48 bg-purple-500/30 rounded-full blur-[60px]"
          animate={{ scale: [1, 1.2, 1], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-56 h-56 bg-cyan-500/30 rounded-full blur-[60px]"
          animate={{ scale: [1.2, 1, 1.2], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-pink-500/20 rounded-full blur-[50px]"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        {/* Glass sidebar */}
        <div className="absolute left-4 top-12 bottom-4 w-40 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 mb-4" />
          {['Home', 'Stats', 'Users', 'Config'].map((label, i) => (
            <div key={i} className={`h-8 rounded-lg mb-2 flex items-center px-2 text-xs ${
              i === 0 ? 'bg-white/10 text-white' : 'text-gray-400'
            }`}>
              {label}
            </div>
          ))}
        </div>
        {/* Glass content cards */}
        <div className="absolute top-12 left-48 right-4 bottom-4 p-4">
          <div className="grid grid-cols-2 gap-3">
            {['Saldo', 'Transações', 'Pendentes', 'Alertas'].map((label, i) => (
              <div key={i} className="h-20 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-3">
                <span className="text-gray-400 text-xs">{label}</span>
                <div className="text-white text-lg font-bold mt-1">R$ {(Math.random() * 10000).toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    NEON_CYBERPUNK: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Neon Cyberpunk</span>
        </div>
        {/* Neon grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,211,238,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />
        {/* Neon header */}
        <div className="absolute top-12 left-4 right-4">
          <motion.div 
            className="h-10 bg-surface-900 border-2 border-cyan-500 rounded-lg flex items-center px-4"
            animate={{ boxShadow: ['0 0 10px rgba(34,211,238,0.3)', '0 0 25px rgba(34,211,238,0.6)', '0 0 10px rgba(34,211,238,0.3)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-cyan-400 text-sm font-bold">CYBER//DASH</span>
          </motion.div>
        </div>
        {/* Neon sidebar */}
        <motion.div 
          className="absolute left-4 top-28 bottom-4 w-36 bg-surface-900 border-2 border-pink-500 rounded-lg p-3"
          animate={{ boxShadow: ['0 0 10px rgba(236,72,153,0.3)', '0 0 20px rgba(236,72,153,0.5)', '0 0 10px rgba(236,72,153,0.3)'] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          {['// HOME', '// DATA', '// USERS', '// SYS'].map((label, i) => (
            <div key={i} className={`text-xs py-2 px-2 mb-1 rounded ${
              i === 0 ? 'bg-pink-500/20 text-pink-400' : 'text-gray-500 hover:text-pink-400'
            }`}>
              {label}
            </div>
          ))}
        </motion.div>
        {/* Neon content cards */}
        <div className="absolute top-28 left-44 right-4 bottom-4 grid grid-cols-2 gap-3">
          {[
            { color: 'cyan', value: '12.5K' },
            { color: 'pink', value: '8.3K' },
            { color: 'yellow', value: '45' },
            { color: 'green', value: '99.9%' },
          ].map((item, i) => (
            <motion.div
              key={i}
              className={`bg-surface-900 border border-${item.color}-500/50 rounded-lg p-3 flex flex-col justify-center`}
              animate={{ 
                boxShadow: [
                  `0 0 5px rgba(${item.color === 'cyan' ? '34,211,238' : item.color === 'pink' ? '236,72,153' : item.color === 'yellow' ? '234,179,8' : '34,197,94'},0.2)`,
                  `0 0 15px rgba(${item.color === 'cyan' ? '34,211,238' : item.color === 'pink' ? '236,72,153' : item.color === 'yellow' ? '234,179,8' : '34,197,94'},0.4)`,
                  `0 0 5px rgba(${item.color === 'cyan' ? '34,211,238' : item.color === 'pink' ? '236,72,153' : item.color === 'yellow' ? '234,179,8' : '34,197,94'},0.2)`,
                ]
              }}
              transition={{ duration: 2, delay: i * 0.5, repeat: Infinity }}
            >
              <span className={`text-${item.color}-400 text-xl font-bold font-mono`}>{item.value}</span>
            </motion.div>
          ))}
        </div>
      </div>
    ),
    AURORA_BOREALIS: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Aurora Borealis</span>
        </div>
        {/* Aurora waves - multiple layers */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(34,211,238,0.15) 20%, rgba(168,85,247,0.2) 40%, rgba(59,130,246,0.15) 60%, transparent 100%)',
          }}
          animate={{ y: [0, -80, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(168,85,247,0.2) 30%, rgba(34,211,238,0.15) 50%, rgba(236,72,153,0.1) 70%, transparent 100%)',
          }}
          animate={{ y: [0, 60, 0], opacity: [0.7, 0.4, 0.7] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(59,130,246,0.1) 25%, rgba(34,211,238,0.2) 50%, rgba(168,85,247,0.15) 75%, transparent 100%)',
          }}
          animate={{ y: [0, -40, 0], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Waves className="w-8 h-8 text-cyan-400" />
            </div>
            <p className="text-white/80 text-sm">Gradientes animados</p>
            <p className="text-white/50 text-xs mt-1">como aurora boreal</p>
          </div>
        </div>
      </div>
    ),
    ISOMETRIC_3D: (
      <div className="w-full h-full bg-gradient-to-br from-surface-950 via-orange-950/10 to-surface-950 relative overflow-hidden" style={{ perspective: '1000px' }}>
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-orange-400 text-xs font-bold uppercase tracking-wider">Isometric 3D</span>
        </div>
        {/* Isometric grid of cards */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="grid grid-cols-2 gap-4"
            style={{ transform: 'rotateX(55deg) rotateZ(-45deg)', transformStyle: 'preserve-3d' }}
          >
            {[
              { color: 'from-orange-500 to-red-500', label: 'Dashboard' },
              { color: 'from-cyan-500 to-blue-500', label: 'Analytics' },
              { color: 'from-purple-500 to-pink-500', label: 'Users' },
              { color: 'from-green-500 to-teal-500', label: 'Reports' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`w-24 h-24 bg-gradient-to-br ${item.color} rounded-xl shadow-2xl flex items-center justify-center`}
                style={{ transformStyle: 'preserve-3d', transform: 'translateZ(20px)' }}
                animate={{ translateZ: [20, 35, 20] }}
                transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
              >
                <span className="text-white text-xs font-bold" style={{ transform: 'rotateZ(45deg) rotateX(-55deg)' }}>
                  {item.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    ),
    PARALLAX_LAYERS: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-20">
          <span className="text-orange-400 text-xs font-bold uppercase tracking-wider">Parallax Layers</span>
        </div>
        {/* Multiple parallax layers */}
        <motion.div 
          className="absolute inset-0 flex items-end justify-center pb-4"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <div className="w-full h-24 bg-gradient-to-t from-cyan-500/10 to-transparent" />
        </motion.div>
        <motion.div 
          className="absolute inset-0 flex items-end justify-center pb-8"
          animate={{ y: [0, -25, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <div className="w-full h-32 bg-gradient-to-t from-purple-500/10 to-transparent" />
        </motion.div>
        <motion.div 
          className="absolute inset-0 flex items-end justify-center pb-12"
          animate={{ y: [0, -35, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          <div className="w-full h-40 bg-gradient-to-t from-blue-500/10 to-transparent" />
        </motion.div>
        {/* Floating content cards at different depths */}
        <div className="absolute inset-0 flex items-center justify-center gap-4 p-8">
          {[
            { depth: 0, delay: 0, color: 'cyan' },
            { depth: 10, delay: 0.5, color: 'purple' },
            { depth: 20, delay: 1, color: 'blue' },
          ].map((layer, i) => (
            <motion.div
              key={i}
              className={`w-28 h-20 bg-surface-800/80 backdrop-blur-sm border border-${layer.color}-500/30 rounded-xl flex items-center justify-center`}
              animate={{ y: [0, -layer.depth - 5, 0] }}
              transition={{ duration: 3 + i, delay: layer.delay, repeat: Infinity }}
            >
              <Layers className={`w-6 h-6 text-${layer.color}-400`} />
            </motion.div>
          ))}
        </div>
      </div>
    ),
    FLOATING_CARDS: (
      <div className="w-full h-full bg-gradient-to-br from-surface-950 via-red-950/5 to-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-red-400 text-xs font-bold uppercase tracking-wider">Floating Cards</span>
        </div>
        {/* Floating cards with dynamic shadows */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-3 gap-4 p-8">
            {[
              { icon: Home, label: 'Home', delay: 0 },
              { icon: BarChart3, label: 'Stats', delay: 0.3 },
              { icon: Users, label: 'Users', delay: 0.6 },
              { icon: FileText, label: 'Docs', delay: 0.9 },
              { icon: Settings, label: 'Config', delay: 1.2 },
              { icon: Bell, label: 'Alerts', delay: 1.5 },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="w-20 h-16 bg-surface-800 rounded-xl flex flex-col items-center justify-center"
                animate={{ 
                  y: [0, -8, 0],
                  boxShadow: [
                    '0 10px 30px rgba(0,0,0,0.3)',
                    '0 20px 40px rgba(0,0,0,0.4)',
                    '0 10px 30px rgba(0,0,0,0.3)',
                  ]
                }}
                transition={{ duration: 2.5, delay: item.delay, repeat: Infinity }}
              >
                <item.icon className="w-5 h-5 text-gray-400 mb-1" />
                <span className="text-gray-500 text-[10px]">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    ),
    PARTICLE_FLOW: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-teal-400 text-xs font-bold uppercase tracking-wider">Particle Flow</span>
        </div>
        {/* Animated particles with connections */}
        <svg className="absolute inset-0 w-full h-full">
          {/* Connection lines */}
          {Array.from({ length: 8 }).map((_, i) => {
            const x1 = 50 + Math.cos(i * 0.8) * 120;
            const y1 = 150 + Math.sin(i * 0.8) * 80;
            const x2 = 250 + Math.cos(i * 0.5 + 1) * 100;
            const y2 = 150 + Math.sin(i * 0.5 + 1) * 60;
            return (
              <motion.line
                key={`line-${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="rgba(34,211,238,0.15)"
                strokeWidth="1"
                animate={{ opacity: [0.1, 0.3, 0.1] }}
                transition={{ duration: 3, delay: i * 0.2, repeat: Infinity }}
              />
            );
          })}
        </svg>
        {/* Floating particles */}
        {Array.from({ length: 25 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-cyan-500"
            style={{ 
              width: 3 + Math.random() * 4,
              height: 3 + Math.random() * 4,
              left: `${10 + Math.random() * 80}%`, 
              top: `${10 + Math.random() * 80}%`,
              opacity: 0.3 + Math.random() * 0.5,
            }}
            animate={{
              x: [0, (Math.random() - 0.5) * 60],
              y: [0, (Math.random() - 0.5) * 60],
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 4 + Math.random() * 4, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Sparkles className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
            <p className="text-white/60 text-xs">Partículas interativas</p>
          </div>
        </div>
      </div>
    ),
    LIQUID_MOTION: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-teal-400 text-xs font-bold uppercase tracking-wider">Liquid Motion</span>
        </div>
        {/* Animated liquid blobs */}
        <motion.div
          className="absolute w-56 h-56 rounded-full blur-[50px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(34,211,238,0.4) 0%, transparent 70%)',
            left: '5%', 
            top: '10%' 
          }}
          animate={{ 
            x: [0, 80, 0], 
            y: [0, 40, 0], 
            scale: [1, 1.3, 1],
            borderRadius: ['50%', '40%', '50%']
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-48 h-48 rounded-full blur-[40px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, transparent 70%)',
            right: '10%', 
            bottom: '15%' 
          }}
          animate={{ 
            x: [0, -60, 0], 
            y: [0, -30, 0], 
            scale: [1.2, 0.9, 1.2],
            borderRadius: ['50%', '45%', '50%']
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-40 h-40 rounded-full blur-[35px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(236,72,153,0.3) 0%, transparent 70%)',
            left: '40%', 
            top: '40%' 
          }}
          animate={{ 
            scale: [1, 1.4, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Center icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center">
            <Droplet className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
      </div>
    ),
    MATRIX_RAIN: (
      <div className="w-full h-full bg-black relative overflow-hidden font-mono">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-green-400 text-xs font-bold uppercase tracking-wider">Matrix Rain</span>
        </div>
        {/* Matrix rain columns */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-green-500 text-xs leading-4 select-none"
            style={{ left: `${i * 5}%`, opacity: 0.3 + Math.random() * 0.5 }}
            animate={{ y: ['-100%', '100%'] }}
            transition={{ 
              duration: 3 + Math.random() * 4, 
              repeat: Infinity, 
              ease: 'linear',
              delay: Math.random() * 2
            }}
          >
            {Array.from({ length: 25 }).map((_, j) => (
              <div key={j} className={j === 0 ? 'text-green-300' : ''}>
                {String.fromCharCode(0x30A0 + Math.floor(Math.random() * 96))}
              </div>
            ))}
          </motion.div>
        ))}
        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/80 px-6 py-4 rounded-lg border border-green-500/30">
            <Binary className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-green-400 text-xs">SYSTEM ONLINE</p>
          </div>
        </div>
      </div>
    ),
    HOLOGRAPHIC: (
      <div className="w-full h-full bg-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-cyan-400 text-xs font-bold uppercase tracking-wider">Holographic</span>
        </div>
        {/* Holographic shimmer background */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(168,85,247,0.15) 20%, rgba(236,72,153,0.15) 40%, rgba(59,130,246,0.15) 60%, rgba(34,211,238,0.15) 80%, rgba(168,85,247,0.15) 100%)',
            backgroundSize: '300% 300%',
          }}
          animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        />
        {/* Holographic cards */}
        <div className="absolute inset-0 flex items-center justify-center gap-4 p-8">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-24 h-32 rounded-xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
              animate={{
                rotateY: [0, 5, 0, -5, 0],
              }}
              transition={{ duration: 4, delay: i * 0.5, repeat: Infinity }}
            >
              <motion.div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                  backgroundSize: '200% 200%',
                }}
                animate={{ backgroundPosition: ['200% 200%', '-100% -100%'] }}
                transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
              />
              <div className="relative z-10 h-full flex flex-col items-center justify-center">
                <Gem className="w-6 h-6 text-white/80 mb-2" />
                <span className="text-white/60 text-xs">Card {i + 1}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    ),
    COMMAND_CENTER: (
      <div className="w-full h-full bg-black relative overflow-hidden font-mono">
        {/* Scan lines effect */}
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.3)_50%)] bg-[size:100%_4px] pointer-events-none opacity-30" />
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,211,238,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.05)_1px,transparent_1px)] bg-[size:20px_20px]" />
        {/* Header bar */}
        <div className="absolute top-0 left-0 right-0 h-8 bg-surface-900/90 border-b border-cyan-500/30 flex items-center px-3">
          <motion.div 
            className="w-2 h-2 rounded-full bg-green-500 mr-2"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <span className="text-cyan-500 text-xs">COMMAND CENTER v2.0</span>
          <span className="ml-auto text-cyan-500/50 text-[10px]">SYS: ONLINE</span>
        </div>
        {/* Main panels */}
        <div className="absolute top-10 left-2 right-2 bottom-2 grid grid-cols-3 gap-2">
          {['NAVIGATION', 'MAIN DISPLAY', 'SYSTEMS'].map((label, i) => (
            <div key={i} className="bg-surface-900/50 border border-cyan-500/30 rounded p-2 flex flex-col">
              <div className="text-cyan-500 text-[10px] mb-2 border-b border-cyan-500/20 pb-1">{label}</div>
              <div className="flex-1 flex items-center justify-center">
                {i === 1 ? (
                  <Monitor className="w-8 h-8 text-cyan-500/50" />
                ) : (
                  <div className="space-y-1 w-full">
                    {[1, 2, 3].map((j) => (
                      <motion.div 
                        key={j}
                        className="h-3 bg-cyan-500/10 rounded"
                        animate={{ opacity: [0.3, 0.6, 0.3] }}
                        transition={{ duration: 2, delay: j * 0.3, repeat: Infinity }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    NEURAL_NETWORK: (
      <div className="w-full h-full bg-gradient-to-br from-surface-950 via-blue-950/10 to-surface-950 relative overflow-hidden">
        {/* Title */}
        <div className="absolute top-4 left-4 z-10">
          <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">Neural Network</span>
        </div>
        {/* Neural network visualization */}
        <svg className="absolute inset-0 w-full h-full">
          {/* Layer 1 nodes */}
          {[80, 140, 200].map((y, i) => (
            <motion.circle
              key={`l1-${i}`}
              cx={80} cy={y} r="12"
              fill="rgba(34,211,238,0.6)"
              animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
            />
          ))}
          {/* Layer 2 nodes */}
          {[60, 110, 160, 210].map((y, i) => (
            <motion.circle
              key={`l2-${i}`}
              cx={180} cy={y} r="10"
              fill="rgba(168,85,247,0.6)"
              animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, delay: 0.5 + i * 0.15, repeat: Infinity }}
            />
          ))}
          {/* Layer 3 nodes */}
          {[90, 150, 210].map((y, i) => (
            <motion.circle
              key={`l3-${i}`}
              cx={280} cy={y} r="10"
              fill="rgba(59,130,246,0.6)"
              animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, delay: 1 + i * 0.2, repeat: Infinity }}
            />
          ))}
          {/* Output node */}
          <motion.circle
            cx={360} cy={150} r="14"
            fill="rgba(34,197,94,0.7)"
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, delay: 1.5, repeat: Infinity }}
          />
          {/* Connections - Layer 1 to 2 */}
          {[80, 140, 200].map((y1, i) => 
            [60, 110, 160, 210].map((y2, j) => (
              <motion.line
                key={`c1-${i}-${j}`}
                x1={92} y1={y1} x2={170} y2={y2}
                stroke="rgba(34,211,238,0.2)"
                strokeWidth="1"
                animate={{ opacity: [0.1, 0.4, 0.1] }}
                transition={{ duration: 1.5, delay: (i + j) * 0.1, repeat: Infinity }}
              />
            ))
          )}
          {/* Connections - Layer 2 to 3 */}
          {[60, 110, 160, 210].map((y1, i) => 
            [90, 150, 210].map((y2, j) => (
              <motion.line
                key={`c2-${i}-${j}`}
                x1={190} y1={y1} x2={270} y2={y2}
                stroke="rgba(168,85,247,0.2)"
                strokeWidth="1"
                animate={{ opacity: [0.1, 0.4, 0.1] }}
                transition={{ duration: 1.5, delay: 0.5 + (i + j) * 0.1, repeat: Infinity }}
              />
            ))
          )}
          {/* Connections - Layer 3 to output */}
          {[90, 150, 210].map((y, i) => (
            <motion.line
              key={`c3-${i}`}
              x1={290} y1={y} x2={346} y2={150}
              stroke="rgba(59,130,246,0.3)"
              strokeWidth="1.5"
              animate={{ opacity: [0.2, 0.6, 0.2] }}
              transition={{ duration: 1.5, delay: 1 + i * 0.2, repeat: Infinity }}
            />
          ))}
        </svg>
        {/* Center label */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <span className="text-white/40 text-xs">Rede neural conectada</span>
        </div>
      </div>
    ),
  };

  const preview = layoutPreviews[layoutType];

  if (!preview) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <Maximize2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Layout não disponível para preview</p>
        </div>
      </div>
    );
  }

  return <>{preview}</>;
}

// Icon mapping for layout types
const layoutIcons: Record<string, React.ReactNode> = {
  BUBBLE_NAVIGATION: <Circle className="w-5 h-5" />,
  ORBITAL: <Orbit className="w-5 h-5" />,
  HEXAGONAL_GRID: <Hexagon className="w-5 h-5" />,
  GLASS_MORPHISM: <Square className="w-5 h-5" />,
  NEON_CYBERPUNK: <Lightbulb className="w-5 h-5" />,
  AURORA_BOREALIS: <Waves className="w-5 h-5" />,
  ISOMETRIC_3D: <Box className="w-5 h-5" />,
  PARALLAX_LAYERS: <Layers className="w-5 h-5" />,
  FLOATING_CARDS: <CreditCard className="w-5 h-5" />,
  PARTICLE_FLOW: <Sparkles className="w-5 h-5" />,
  LIQUID_MOTION: <Droplet className="w-5 h-5" />,
  MATRIX_RAIN: <Binary className="w-5 h-5" />,
  HOLOGRAPHIC: <Gem className="w-5 h-5" />,
  COMMAND_CENTER: <Monitor className="w-5 h-5" />,
  NEURAL_NETWORK: <Brain className="w-5 h-5" />,
};

// Category colors
const categoryColors: Record<LayoutCategory, string> = {
  BASIC: 'from-gray-500 to-gray-600',
  NAVIGATION: 'from-blue-500 to-cyan-500',
  VISUAL_EFFECTS: 'from-purple-500 to-pink-500',
  '3D_DEPTH': 'from-orange-500 to-red-500',
  ANIMATIONS: 'from-green-500 to-teal-500',
  SCIFI: 'from-cyan-500 to-blue-500',
};

interface FuturisticLayoutGalleryProps {
  selectedLayout?: FuturisticLayoutType;
  onSelectLayout: (layout: FuturisticLayoutType) => void;
  tenantId?: string;
  showApplyButton?: boolean;
}

export function FuturisticLayoutGallery({
  selectedLayout,
  onSelectLayout,
  tenantId,
  showApplyButton = true,
}: FuturisticLayoutGalleryProps) {
  const [activeCategory, setActiveCategory] = useState<LayoutCategory | 'ALL'>('ALL');
  const [previewLayout, setPreviewLayout] = useState<FuturisticLayoutType | null>(null);
  const [showFullPreview, setShowFullPreview] = useState(false);

  const { data: templates, isLoading } = useLayoutTemplates();
  const applyTemplate = useApplyLayoutTemplate();

  // Filter layouts by category
  const filteredLayouts = activeCategory === 'ALL'
    ? LAYOUT_METADATA.filter(l => isFuturisticLayout(l.type))
    : getLayoutsByCategory(activeCategory).filter(l => isFuturisticLayout(l.type));

  const handleApplyLayout = async (layoutType: FuturisticLayoutType) => {
    if (!tenantId) return;
    
    const template = templates?.find(t => t.layoutType === layoutType);
    if (template) {
      try {
        await applyTemplate.mutateAsync({ tenantId, templateId: template.id });
        onSelectLayout(layoutType);
      } catch (error) {
        console.error('Failed to apply layout:', error);
      }
    } else {
      onSelectLayout(layoutType);
    }
  };

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeCategory === 'ALL'
              ? 'bg-primary-600 text-white'
              : 'bg-surface-800 text-gray-400 hover:text-white hover:bg-surface-700'
          }`}
        >
          Todos ({LAYOUT_METADATA.filter(l => isFuturisticLayout(l.type)).length})
        </button>
        {(Object.keys(LAYOUT_CATEGORIES) as LayoutCategory[])
          .filter(cat => cat !== 'BASIC')
          .map((category) => {
            const count = getLayoutsByCategory(category).filter(l => isFuturisticLayout(l.type)).length;
            const categoryInfo = LAYOUT_CATEGORIES[category];
            return (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-800 text-gray-400 hover:text-white hover:bg-surface-700'
                }`}
              >
                {categoryInfo.name} ({count})
              </button>
            );
          })}
      </div>

      {/* Layout Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence mode="popLayout">
          {filteredLayouts.map((layout, index) => {
            const isSelected = selectedLayout === layout.type;
            const categoryInfo = LAYOUT_CATEGORIES[layout.category];
            
            return (
              <motion.div
                key={layout.type}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className={`relative group rounded-xl border-2 overflow-hidden transition-all cursor-pointer ${
                  isSelected
                    ? 'border-primary-500 ring-2 ring-primary-500/30'
                    : 'border-surface-700 hover:border-surface-600'
                }`}
                onClick={() => onSelectLayout(layout.type)}
              >
                {/* Preview Background */}
                <div 
                  className={`h-32 bg-gradient-to-br ${categoryColors[layout.category]} opacity-20`}
                />
                
                {/* Content */}
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div 
                      className={`p-2 rounded-lg bg-gradient-to-br ${categoryColors[layout.category]}`}
                    >
                      {layoutIcons[layout.type] || <Sparkles className="w-5 h-5 text-white" />}
                    </div>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="p-1 rounded-full bg-primary-500"
                      >
                        <Check className="w-4 h-4 text-white" />
                      </motion.div>
                    )}
                  </div>

                  {/* Info */}
                  <div>
                    <h4 className="font-semibold text-white mb-1">{layout.name}</h4>
                    <p className="text-xs text-gray-400 line-clamp-2">{layout.description}</p>
                  </div>
                </div>

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewLayout(layout.type);
                      setShowFullPreview(true);
                    }}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  {showApplyButton && tenantId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyLayout(layout.type);
                      }}
                      disabled={applyTemplate.isPending}
                      className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {applyTemplate.isPending ? 'Aplicando...' : 'Aplicar'}
                    </button>
                  )}
                </div>

                {/* Category Badge */}
                <div className="absolute top-2 right-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-surface-800/80 text-gray-300">
                    {categoryInfo.name}
                  </span>
                </div>

                {/* Premium Badge */}
                {layout.isPremium && (
                  <div className="absolute bottom-2 right-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium">
                      Premium
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Full Preview Modal */}
      <AnimatePresence>
        {showFullPreview && previewLayout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowFullPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-surface-900 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-surface-700">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${categoryColors[LAYOUT_METADATA.find(l => l.type === previewLayout)?.category || 'BASIC']}`}>
                    {layoutIcons[previewLayout]}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {LAYOUT_METADATA.find(l => l.type === previewLayout)?.name}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {LAYOUT_METADATA.find(l => l.type === previewLayout)?.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {showApplyButton && tenantId && (
                    <button
                      onClick={() => {
                        handleApplyLayout(previewLayout);
                        setShowFullPreview(false);
                      }}
                      className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-medium transition-colors"
                    >
                      Aplicar Layout
                    </button>
                  )}
                  <button
                    onClick={() => setShowFullPreview(false)}
                    className="p-2 rounded-lg bg-surface-800 hover:bg-surface-700 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview Area */}
              <div className="h-[70vh] bg-surface-950 overflow-hidden">
                <LayoutPreview layoutType={previewLayout} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      )}
    </div>
  );
}

export default FuturisticLayoutGallery;
